import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Organization as DbOrganization } from '../../types/database';

export type Organization = DbOrganization;

interface OrganizationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (organization: Omit<Organization, 'id' | 'created_at' | 'updated_at'>) => void;
  organization?: Organization | null;
}

const ORGANIZATION_SIZES = [
  '1-10',
  '11-50',
  '51-200',
  '201-1000',
  '1000+'
];

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Manufacturing',
  'Retail',
  'Consulting',
  'Non-profit',
  'Government',
  'Other'
];

export const OrganizationModal: React.FC<OrganizationModalProps> = ({
  open,
  onClose,
  onSubmit,
  organization,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    industry: '',
    size: '',
    location: '',
    user_id: '',
  });

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name,
        description: organization.description || '',
        website: organization.website || '',
        industry: organization.industry || '',
        size: organization.size || '',
        location: organization.location || '',
        user_id: organization.user_id,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        website: '',
        industry: '',
        size: '',
        location: '',
        user_id: '',
      });
    }
  }, [organization]);

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = () => {
    console.log('Submitting organization data:', formData);
    const organizationData = {
      name: formData.name?.trim() || '',
      description: formData.description?.trim() || undefined,
      website: formData.website?.trim() || undefined,
      industry: formData.industry?.trim() || undefined,
      size: formData.size?.trim() || undefined,
      location: formData.location?.trim() || undefined,
      user_id: formData.user_id,
    };
    console.log('Processed organization data:', organizationData);
    onSubmit(organizationData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {organization ? 'Edit Organization' : 'Add New Organization'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Organization Name"
            name="name"
            value={formData.name}
            onChange={handleFormChange}
            required
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleFormChange}
            multiline
            rows={3}
            placeholder="Tell us about this organization..."
          />
          <TextField
            fullWidth
            label="Website"
            name="website"
            value={formData.website}
            onChange={handleFormChange}
            placeholder="https://example.com"
          />
          <FormControl fullWidth>
            <InputLabel>Industry</InputLabel>
            <Select
              name="industry"
              value={formData.industry}
              onChange={handleSelectChange}
              label="Industry"
            >
              <MenuItem value="">
                <em>Select an industry</em>
              </MenuItem>
              {INDUSTRIES.map((industry) => (
                <MenuItem key={industry} value={industry}>
                  {industry}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Organization Size</InputLabel>
            <Select
              name="size"
              value={formData.size}
              onChange={handleSelectChange}
              label="Organization Size"
            >
              <MenuItem value="">
                <em>Select organization size</em>
              </MenuItem>
              {ORGANIZATION_SIZES.map((size) => (
                <MenuItem key={size} value={size}>
                  {size} employees
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleFormChange}
            placeholder="City, State/Country"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={!formData.name}
        >
          {organization ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
